import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";

import { getYearlyPlan, savePlan, getSavedPlans } from "../../actions/postAction";
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import Loader from "../../layout/Loder";
import Note from "./Note";

import "./MyNotes.css";
import "./YearPlanDashboard.css";
import "./YearPlanDashboard.dark.css";
import PlanSettingsModal from "./PlanSettingsModal";

const MS_IN_DAY = 1000 * 60 * 60 * 24;

/* ---------------- DELETE MODAL ---------------- */
const DeleteConfirmModal = ({ planName, onConfirm, onClose }) => {
  const [input, setInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const isMatch = input.trim().toLowerCase() === "delete";

  const handleConfirm = async () => {
    if (deleting || !isMatch) return;
    setDeleting(true);
    try {
      await onConfirm();
    } catch (_) {
      setDeleting(false);
    }
  };

  return (
    <div className="dcm-overlay" onClick={deleting ? undefined : onClose}>
      <div className="dcm-modal" onClick={(e) => e.stopPropagation()}>
        <p className="dcm-warning">
          This will permanently delete <strong>{planName || "this vision"}</strong> and all its records. This cannot be undone.
        </p>
        <p className="dcm-hint">Type <strong>delete</strong> to confirm</p>
        <input
          type="text"
          className="dcm-input"
          placeholder="delete"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={deleting}
          autoFocus
        />
        <div className="dcm-footer">
          <button className="dcm-cancel" onClick={onClose} disabled={deleting}>Cancel</button>
          <button
            className="dcm-confirm"
            disabled={!isMatch || deleting}
            onClick={handleConfirm}
          >
            {deleting ? "Deleting..." : "Delete Vision"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const YearPlanView = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { plan } = useSelector((state) => state.yearlyPlan);
  const { user } = useSelector((state) => state.user);
  const { savedPlans } = useSelector((state) => state.savedPlans);

  const [isSaved, setIsSaved] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  const [isLoaded, setIsLoaded]                 = useState(false);

  /* isOwner derived early — used in effects below */
  const isOwner = !!(
    user && plan && plan.user &&
    user._id?.toString() === plan.user?.toString()
  );
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [showSettings, setShowSettings]         = useState(false);
  const [showNewVisionModal, setShowNewVisionModal] = useState(false);
  const [nvmCountdown, setNvmCountdown]         = useState(10);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "thoughts" | "priority"
  const nvmNavigatedRef = useRef(false);

  useEffect(() => {
    if (!plan?._id) return;
    try {
      if (localStorage.getItem(`dmyor_new_vision_${plan._id}`) === "1") {
        setShowNewVisionModal(true);
      }
    } catch (_) {}
  }, [plan?._id]);

  useEffect(() => {
    if (!showNewVisionModal) return;
    const interval = setInterval(() => setNvmCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(interval);
  }, [showNewVisionModal]);

  useEffect(() => {
    if (nvmCountdown === 0 && showNewVisionModal) goToRecords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nvmCountdown]);

  const dismissNewVisionModal = () => {
    if (plan?._id) {
      try { localStorage.removeItem(`dmyor_new_vision_${plan._id}`); } catch (_) {}
    }
    setShowNewVisionModal(false);
  };

  const goToRecords = () => {
    if (nvmNavigatedRef.current) return;
    nvmNavigatedRef.current = true;
    dismissNewVisionModal();
    navigate(`/dashboard/start/vision-setup/${plan._id}?fresh=1`);
  };

  const handleToggleSave = async () => {
    if (!user || savingPlan) return;
    setSavingPlan(true);
    const saved = await dispatch(savePlan(plan._id));
    if (saved !== undefined) setIsSaved(saved);
    setSavingPlan(false);
  };

  const handleVisibilityChange = async (val) => {
    try {
      await axios.patch(
        `${config.apiUrl}/api/v1/yearly-plan/${plan._id}/visibility`,
        { visibility: val },
        { withCredentials: true }
      );
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  useEffect(() => {
    setIsLoaded(false);
    dispatch(getYearlyPlan(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (user && !isOwner) dispatch(getSavedPlans());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isOwner]);

  useEffect(() => {
    if (plan?._id && savedPlans?.length) {
      setIsSaved(savedPlans.some((p) => p._id?.toString() === plan._id?.toString()));
    }
  }, [plan?._id, savedPlans]);

  useEffect(() => {
    if (plan) setIsLoaded(true);
  }, [plan]);

  /* ---------------- PRE-COMPUTE ---------------- */

  const durationInYears = plan?.durationInYears || 5;
  const totalDays       = durationInYears * 365;
  const startDate       = plan?.createdAt ? new Date(plan.createdAt) : new Date();
  const today           = new Date();
  const daysPassed      = Math.floor((today - startDate) / MS_IN_DAY);
  const daysRemaining   = Math.max(totalDays - daysPassed, 0);
  const progressPercent = Math.min(Math.max((daysPassed / totalDays) * 100, 0), 100);

  const posts         = plan?.posts ? plan.posts.map((p) => p.postId) : [];
  const priorityPosts = posts.filter((p) => p && p.isPriority);
  const thoughtsPosts = posts.filter((p) => p && p.category === "Thoughts");
  const displayPosts  =
    filterMode === "priority" ? priorityPosts :
    filterMode === "thoughts" ? thoughtsPosts :
    posts;

  const startDateStr = startDate.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const endDate = new Date(startDate);
  endDate.setFullYear(endDate.getFullYear() + durationInYears);
  const endDateStr = endDate.toLocaleDateString("en-US", { month: "short", year: "numeric" });

  /* ---------------- RENDER ---------------- */
  return (
    <div className="five-year-dashboard dark">
      {!isLoaded ? (
        <Loader />
      ) : (
        <div>
          <Helmet>
            <title>{plan?.heading || plan?.name || "Vision"}</title>
          </Helmet>

          {/* HEADER */}
          <div className="ypd-header-row">
            <div className="ypd-header-left">
              <div className="ypd-meta-row">
                <span className="ypd-duration-badge">{durationInYears} Year Vision</span>
                {plan?.category && (
                  <span className="ypd-category-badge">{plan.category}</span>
                )}
              </div>
              <h1 className="ypd-heading">
                {plan?.heading || plan?.name || "Untitled Vision"}
              </h1>
              {plan?.description && (
                <p className="ypd-description">{plan.description}</p>
              )}
            </div>

            {!isOwner && user && (
              <div className="ypd-header-actions">
                <button
                  className={`plan-report-btn${isSaved ? " plan-report-btn--saved" : ""}`}
                  onClick={handleToggleSave}
                  disabled={savingPlan}
                  title={isSaved ? "Unsave vision" : "Save vision"}
                >
                  <i className={`bx ${isSaved ? "bxs-bookmark" : "bx-bookmark"}`} />
                  {isSaved ? "Saved" : "Save Vision"}
                </button>
              </div>
            )}

            {isOwner && (
              <div className="ypd-header-actions">
                <button
                  className="plan-report-btn"
                  onClick={() => navigate(`/dashboard/start/vision-setup/${plan._id}`)}
                  title="Edit Vision"
                >
                  <i className="bx bx-edit-alt" /> Edit Vision
                </button>
                <button
                  className="plan-report-btn plan-report-btn--todos"
                  onClick={() => navigate(`/dashboard/year-plan/${plan._id}/todos`)}
                  title="Vision Todos"
                >
                  <i className="bx bx-check-square" /> Todos
                </button>
                <button
                  className="plan-report-btn"
                  onClick={() => navigate(`/dashboard/year-plan/${plan._id}/analysis`)}
                  title="Plan Health Report"
                >
                  <i className="bx bx-bar-chart-alt-2" /> Report
                </button>
                <button
                  className="plan-settings-btn"
                  onClick={() => setShowSettings(true)}
                  aria-label="Vision settings"
                >
                  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 2L17.3 6v8L10 18 2.7 14V6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* NEW VISION MODAL */}
          {isOwner && showNewVisionModal && (
            <div className="nvm-overlay" onClick={dismissNewVisionModal}>
              <div className="nvm-card" onClick={(e) => e.stopPropagation()}>
                <div className="nvm-icon" aria-hidden="true">
                  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="10" fill="#111" opacity="0.9" />
                    <circle cx="32" cy="32" r="22" stroke="#111" strokeWidth="1" opacity="0.1" />
                    <circle cx="32" cy="10" r="4" fill="#111" opacity="0.7" />
                    <circle cx="51" cy="21" r="4" fill="#111" opacity="0.7" />
                    <circle cx="51" cy="43" r="4" fill="#111" opacity="0.7" />
                    <circle cx="32" cy="54" r="4" fill="#111" opacity="0.7" />
                    <circle cx="13" cy="43" r="4" fill="#111" opacity="0.7" />
                    <circle cx="13" cy="21" r="4" fill="#111" opacity="0.7" />
                    <line x1="32" y1="32" x2="32" y2="14" stroke="#111" strokeWidth="0.8" opacity="0.3" />
                    <line x1="32" y1="32" x2="48" y2="23" stroke="#111" strokeWidth="0.8" opacity="0.3" />
                    <line x1="32" y1="32" x2="48" y2="41" stroke="#111" strokeWidth="0.8" opacity="0.3" />
                    <line x1="32" y1="32" x2="32" y2="50" stroke="#111" strokeWidth="0.8" opacity="0.3" />
                    <line x1="32" y1="32" x2="16" y2="41" stroke="#111" strokeWidth="0.8" opacity="0.3" />
                    <line x1="32" y1="32" x2="16" y2="23" stroke="#111" strokeWidth="0.8" opacity="0.3" />
                  </svg>
                </div>
                <p className="nvm-eyebrow">Vision created</p>
                <h2 className="nvm-heading">Now, add your Records.</h2>
                <p className="nvm-body">
                  Records are the specific things you will do to make this vision real.
                  Not plans. Not goals. The actual work — each one tracked, updated, and analyzed with AI.
                </p>
                <div className="nvm-actions">
                  <button type="button" className="nvm-cta" onClick={goToRecords}>
                    Add Records now
                    <span className="nvm-arrow">→</span>
                  </button>
                  <div className="nvm-countdown" title={`Continuing in ${nvmCountdown}s`}>
                    <svg width="44" height="44" viewBox="0 0 44 44">
                      <circle cx="22" cy="22" r="18" stroke="rgba(0,0,0,0.1)" strokeWidth="2" fill="none" />
                      <circle
                        cx="22" cy="22" r="18"
                        stroke="#111"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={113.1}
                        strokeDashoffset={113.1 * (nvmCountdown / 10)}
                        transform="rotate(-90 22 22)"
                        style={{ transition: "stroke-dashoffset 0.95s linear" }}
                      />
                      <text x="22" y="26" textAnchor="middle" fontSize="10" fontWeight="600" fill="#111" fontFamily="inherit">
                        {nvmCountdown}
                      </text>
                    </svg>
                  </div>
                </div>
                <button type="button" className="nvm-skip" onClick={dismissNewVisionModal}>
                  I will do this later
                </button>
              </div>
            </div>
          )}

          {/* TIME BAR */}
          <div className="ypd-time-bar-wrap">
            <div className="ypd-time-bar">
              <div className="ypd-time-bar-fill" style={{ width: `${progressPercent}%` }} />
            </div>
            <div className="ypd-time-bar-labels">
              <span className="ypd-time-start">{startDateStr}</span>
              <span className="ypd-time-remaining">
                <strong>{daysRemaining.toLocaleString()}</strong> days remaining
              </span>
              <span className="ypd-time-end">{endDateStr}</span>
            </div>
          </div>

          {/* SPACE VIEW */}
          <button
            className="space-view-btn"
            onClick={() => {
              localStorage.setItem("dmyor_gs_spaceview", "1");
              navigate(`/dashboard/plan-cosmos/${plan._id}`);
            }}
          >
            <div className="space-view-stars" />
            <div className="space-view-stars space-view-stars-2" />
            <div className="space-view-stars space-view-stars-3" />
            <span className="space-view-label">Space View</span>
          </button>

          {/* RECORDS TOGGLE + LIST */}
          <div className="ypd-records-header">
            <div className="ypd-filter-toggle">
              <button
                className={`ypd-filter-btn${filterMode === "all" ? " active" : ""}`}
                onClick={() => setFilterMode("all")}
              >
                All
              </button>
              <button
                className={`ypd-filter-btn${filterMode === "thoughts" ? " active" : ""}`}
                onClick={() => setFilterMode("thoughts")}
              >
                Thoughts
              </button>
              <button
                className={`ypd-filter-btn${filterMode === "priority" ? " active" : ""}`}
                onClick={() => setFilterMode("priority")}
              >
                Priority
              </button>
            </div>
            {isOwner && (
              <button
                className="ypd-add-record-btn"
                onClick={() => navigate(`/dashboard/start/vision-setup/${plan._id}`)}
              >
                + Add Record
              </button>
            )}
          </div>

          <div className="dashboard-section">
            {displayPosts.length === 0 ? (
              filterMode === "priority" ? (
                <p className="empty-state">No priority records yet.</p>
              ) : filterMode === "thoughts" ? (
                <p className="empty-state">No thoughts record yet.</p>
              ) : (
                <div className="ypd-empty-state">
                  <button
                    className="ypd-record-box"
                    onClick={isOwner ? () => navigate(`/dashboard/start/vision-setup/${plan._id}`) : undefined}
                    style={!isOwner ? { cursor: "default" } : undefined}
                  >
                    <div className="ypd-record-box-top">
                      <span className="ypd-record-box-label">record</span>
                    </div>
                    <div className="ypd-record-box-body">
                      <span className="ypd-record-box-cursor" />
                      <span className="ypd-record-box-placeholder">what are we doing to make this vision a reality...</span>
                    </div>
                    <div className="ypd-record-box-foot">
                      <span className="ypd-record-box-def">a specific thing you are doing to make this vision real</span>
                      {isOwner && <span className="ypd-record-box-cta">Begin →</span>}
                    </div>
                  </button>
                </div>
              )
            ) : (
              <div className="ypd-notes-list">
                {displayPosts.map((post) => (
                  <Note key={post._id} post={post} dark={true} isOwner={isOwner} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showSettings && (
        <PlanSettingsModal
          plan={plan}
          onClose={() => setShowSettings(false)}
          onVisibilityChange={handleVisibilityChange}
          onDeleteRequest={() => {
            setShowSettings(false);
            setShowDeleteModal(true);
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmModal
          planName={plan?.name}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async () => {
            try {
              await axios.delete(`${config.apiUrl}/api/v1/yearly-plan/${plan._id}`, { withCredentials: true });
              toast.success("Vision deleted");
              navigate("/dashboard");
            } catch (err) {
              toast.error(err.response?.data?.message || "Failed to delete vision");
              throw err;
            }
          }}
        />
      )}
    </div>
  );
};

export default YearPlanView;
